import React, { ReactElement, useEffect, useMemo } from 'react';

import { BUILD_EVENT, TComponent } from '@core';
import { DEFAULT_FORM_HOOK_ID, EEVents } from '@core/constants';

import { useForceUpdate } from '@react/useForceUpdate';
import { getFormInstance } from '@core/managers';
import { TAsFormFieldProps, TDecoratorProps } from '../react/types';

const asFormField =
  <OriginalComponentProps,>({ Comp, propsMapping }: TAsFormFieldProps) =>
  ({
    name,
    validations,
    errorMessages,
    masks,
    clearFields,
    api,
    filter,
    formatters,
    visibilityConditions,
    formId = DEFAULT_FORM_HOOK_ID,
    visibility = true,
    value,
    disabled = false,
    optionList = [],
    onSelected,
    ...props
  }: TDecoratorProps<OriginalComponentProps>): ReactElement => {
    const [, forceUpdate] = useForceUpdate();
    const component: TComponent = useMemo(
      () => ({
        state: {
          hidden: !visibility,
        },
        component: '',
        name,
        validations,
        errorMessages,
        masks,
        filter,
        visibilityConditions,
        formatters,
        clearFields,
        api,
      }),
      [
        errorMessages,
        name,
        filter,
        formatters,
        masks,
        validations,
        visibilityConditions,
        clearFields,
        api,
      ],
    );
    const fieldInstance = useMemo(
      () => getFormInstance(formId).getFieldInstance(component, propsMapping),
      [],
    );
    const getValue = useMemo(
      () => props[propsMapping.getValue as string],
      [props],
    );
    const onBlur = useMemo(() => props[propsMapping.onBlur as string], [props]);
    const onClick = useMemo(
      () => props[propsMapping.onClick as string],
      [props],
    );
    const onFocus = useMemo(
      () => props[propsMapping.onFocus as string],
      [props],
    );
    const onKeyUp = useMemo(
      () => props[propsMapping.onKeyUp as string],
      [props],
    );
    const onKeyDown = useMemo(
      () => props[propsMapping.onKeyDown as string],
      [props],
    );

    useMemo(() => {
      fieldInstance.publish(
        BUILD_EVENT(EEVents.ON_FIELD_MOUNT, component.name),
      );
    }, []);

    useMemo(() => {
      fieldInstance.subscribeBulk(
        [
          BUILD_EVENT(EEVents.ON_FIELD_MOUNT, component.name),
          BUILD_EVENT(EEVents.ON_FIELD_REHYDRATE, component.name),
        ],
        () => {
          forceUpdate();
          onSelected && onSelected(fieldInstance.data);
        },
      );
    }, []);

    useMemo(() => {
      fieldInstance.subscribe(
        BUILD_EVENT(EEVents.ON_FIELD_CHANGE, component.name),
        ({ data }) => {
          forceUpdate();
          getValue && getValue(data.event, fieldInstance.data);
          onSelected && onSelected(fieldInstance.data);
        },
      );
    }, [getValue]);

    useMemo(() => {
      fieldInstance.subscribe(
        BUILD_EVENT(EEVents.ON_FIELD_BLUR, component.name),
        ({ data }) => {
          forceUpdate();
          onBlur && onBlur(data.event);
        },
      );
    }, [onBlur]);

    useMemo(() => {
      fieldInstance.subscribe(
        BUILD_EVENT(EEVents.ON_FIELD_CLICK, component.name),
        ({ data }) => {
          forceUpdate();
          onClick && onClick(data.event);
        },
      );
    }, [onClick]);

    useMemo(() => {
      fieldInstance.subscribe(
        BUILD_EVENT(EEVents.ON_FIELD_FOCUS, component.name),
        ({ data }) => {
          forceUpdate();
          onFocus && onFocus(data.event);
        },
      );
    }, [onFocus]);

    useMemo(() => {
      fieldInstance.subscribe(
        BUILD_EVENT(EEVents.ON_FIELD_KEYUP, component.name),
        ({ data }) => {
          forceUpdate();
          onKeyUp && onKeyUp(data.event);
        },
      );
    }, [onKeyUp]);

    useMemo(() => {
      fieldInstance.subscribe(
        BUILD_EVENT(EEVents.ON_FIELD_KEYDOWN, component.name),
        ({ data }) => {
          forceUpdate();
          onKeyDown && onKeyDown(data.event);
        },
      );
    }, [onKeyDown]);

    useEffect(() => {
      if (typeof value === 'undefined') return;
      fieldInstance.value = value;
      fieldInstance.rehydrate();
    }, [value]);

    useEffect(() => {
      fieldInstance.visibility = visibility;
      fieldInstance.rehydrate();
    }, [visibility]);

    useEffect(() => {
      fieldInstance.componentProps = {
        ...fieldInstance.componentProps,
        disabled,
      };
    }, [disabled]);

    useEffect(() => {
      fieldInstance.componentProps = {
        ...fieldInstance.componentProps,
        optionList,
      };
      fieldInstance.rehydrate(JSON.stringify(optionList));
    }, [optionList]);

    useEffect(() => {
      return () => getFormInstance(formId).destroyField(name);
    }, []);

    if (!fieldInstance.data.visible) {
      return <></>;
    }

    return <Comp {...props} {...fieldInstance.props} />;
  };

export default asFormField;
